/**
 * LandingPage for the judge interface.
 *
 * Shows:
 * - Active games (in progress)
 * - Game queue (upcoming assigned games)
 * - Messages from admin
 * - Finished games history
 */

import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import PersonIcon from '@mui/icons-material/Person';
import PlayIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { judgeApi } from '@/api/client';
import { MessageList } from '@/features/judge/components/MessageList';
import { useGameState } from '@/stores/gameStore';
import { useAuthStore } from '@/stores/authStore';
import { generatePaperId, getHalfDisplayScore } from '@/types';
import type { EventType, JudgeQueueGame, LocalGameState } from '@/types';

/** Calculate total scores for a finished game and determine winner (higher score wins) */
function getGameResult(game: LocalGameState): {
  team1Total: number;
  team2Total: number;
  winner: 'team1' | 'team2' | 'draw' | null;
} {
  if (game.overtime.ready) {
    const t1 = getHalfDisplayScore(game.overtime.team1);
    const t2 = getHalfDisplayScore(game.overtime.team2);
    return {
      team1Total: t1,
      team2Total: t2,
      winner: t1 > t2 ? 'team1' : t1 < t2 ? 'team2' : 'draw',
    };
  }

  if (game.firstHalf.ready && game.secondHalf.ready) {
    const t1 = getHalfDisplayScore(game.firstHalf.team1) + getHalfDisplayScore(game.secondHalf.team1);
    const t2 = getHalfDisplayScore(game.firstHalf.team2) + getHalfDisplayScore(game.secondHalf.team2);
    return {
      team1Total: t1,
      team2Total: t2,
      winner: t1 > t2 ? 'team1' : t1 < t2 ? 'team2' : 'draw',
    };
  }

  return { team1Total: 0, team2Total: 0, winner: null };
}

export function LandingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { games, finishedGames, startGame, reopenGame } = useGameState();
  const { user } = useAuthStore();
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [gameToReopen, setGameToReopen] = useState<LocalGameState | null>(null);
  const [reopenError, setReopenError] = useState<string | null>(null);

  // Filter finished games by current user (show games without userId for backwards compatibility)
  const userFinishedGames = finishedGames.filter(
    (game) => !game.userId || game.userId === user?.id
  );

  // Fetch game queue
  const {
    data: queueGames,
    isLoading: isLoadingQueue,
    error: queueError,
  } = useQuery({
    queryKey: ['judgeQueue'],
    queryFn: judgeApi.getQueue,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch messages
  const { data: messages = [] } = useQuery({
    queryKey: ['judgeMessages'],
    queryFn: judgeApi.getMessages,
    refetchInterval: 60000, // Refresh every minute
  });

  // Mutation to submit game_start event
  const submitStartMutation = useMutation({
    mutationFn: judgeApi.submitEvent,
    onSuccess: () => {
      // Invalidate queue so it refetches without the started game
      queryClient.invalidateQueries({ queryKey: ['judgeQueue'] });
    },
  });

  // Mutation to submit game_reopen event
  const submitReopenMutation = useMutation({
    mutationFn: judgeApi.submitEvent,
  });

  const handleStartQueueGame = (queueGame: JudgeQueueGame) => {
    // Don't allow starting new game if there's already an active one
    if (games.length > 0) {
      // Navigate to the existing active game instead
      navigate(`/judge/game/${games[0].id}`);
      return;
    }

    // Defensive check - don't start games with undefined teams
    if (!queueGame.team1_id || !queueGame.team2_id || !queueGame.team1_name || !queueGame.team2_name) {
      return;
    }
    
    const paperId = generatePaperId(queueGame.paper_id);
    
    // Create local game state (writes to localStorage synchronously)
    startGame(
      paperId,
      queueGame.paper_id,
      queueGame.round,
      queueGame.league_id,
      { id: queueGame.team1_id, name: queueGame.team1_name },
      { id: queueGame.team2_id, name: queueGame.team2_name },
      user?.id
    );
    
    // Submit game_start event to backend (fire and forget)
    submitStartMutation.mutate({
      paper_id: paperId,
      team1_id: queueGame.team1_id,
      team2_id: queueGame.team2_id,
      event: 'game_start' as EventType,
    });
    
    // Navigate immediately - localStorage is already written
    navigate(`/judge/game/${paperId}`);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fi-FI', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenReopenDialog = (game: LocalGameState) => {
    setGameToReopen(game);
    setReopenDialogOpen(true);
  };

  const handleCloseReopenDialog = () => {
    setReopenDialogOpen(false);
    setGameToReopen(null);
    setReopenError(null);
  };

  const handleConfirmReopen = async () => {
    if (!gameToReopen) return;

    setReopenError(null);

    try {
      // Wait for backend to succeed before updating local state
      await submitReopenMutation.mutateAsync({
        paper_id: gameToReopen.id,
        team1_id: gameToReopen.team1.id,
        team2_id: gameToReopen.team2.id,
        event: 'game_reopen' as EventType,
      });

      // Only update local state if backend succeeded
      const reopenedGame = reopenGame(gameToReopen.id);
      if (reopenedGame) {
        navigate(`/judge/game/${reopenedGame.id}`);
      }

      handleCloseReopenDialog();
    } catch (error) {
      // Show error - don't update local state
      // Game stays in finishedGames where it belongs
      const axiosError = error as AxiosError<{ message?: string }>;
      const backendMessage = axiosError.response?.data?.message;
      setReopenError(backendMessage || 'Pelin avaaminen epäonnistui. Yritä uudelleen.');
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      {/* Header with title and profile button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          Tuloskilke
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonIcon />}
          component={RouterLink}
          to="/profile"
          sx={{ minHeight: 40 }}
        >
          Omat tiedot
        </Button>
      </Box>

      {/* Active Games */}
      {games.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Jatka peliä
          </Typography>
          <List disablePadding>
            {games.map((game, index) => (
              <ListItem
                key={game.id}
                disablePadding
                sx={index === 0 ? {
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 1,
                  bgcolor: 'success.50',
                  mb: 1,
                } : undefined}
              >
                <ListItemButton
                  component={RouterLink}
                  to={`/judge/game/${game.id}`}
                  sx={{
                    py: 1.5,
                    minHeight: 56,
                    ...(index === 0 && {
                      '&:hover': {
                        bgcolor: 'success.100',
                      },
                    }),
                  }}
                >
                  <PlayIcon color="success" sx={{ mr: 2 }} />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography component="span" fontWeight={index === 0 ? 700 : 400}>
                          {game.id}: {game.team1.name} vs {game.team2.name}
                        </Typography>
                        {index === 0 && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              bgcolor: 'success.main',
                              color: 'white',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 700,
                            }}
                          >
                            JATKA
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={`Aloitettu ${formatTime(game.startTime)}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* New Game Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Uuden pelin aloittaminen
        </Typography>

        {/* Warning if there's an active game */}
        {games.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sinulla on keskeneräinen peli. Päätä se ennen uuden aloittamista.
          </Alert>
        )}

        {/* Game Queue */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Tulevat pelit
        </Typography>

        {isLoadingQueue ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : queueError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Pelien haku epäonnistui
          </Alert>
        ) : queueGames && queueGames.length > 0 ? (
          <List
            disablePadding
            sx={{
              mb: 2,
              // Grey out the entire list when there's an active game
              ...(games.length > 0 && {
                opacity: 0.5,
                filter: 'grayscale(50%)',
              }),
            }}
          >
            {queueGames
              // Filter out games with undefined teams (defensive - backend should already filter)
              .filter((g) => g.team1_id && g.team2_id)
              .map((queueGame, index) => {
              // Only highlight first queue game if no active games
              const shouldHighlight = index === 0 && games.length === 0;
              return (
              <ListItem
                key={queueGame.id}
                disablePadding
                sx={shouldHighlight ? {
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  bgcolor: 'primary.50',
                  mb: 1,
                } : undefined}
              >
                <ListItemButton
                  onClick={() => handleStartQueueGame(queueGame)}
                  disabled={games.length > 0}
                  sx={{
                    py: 1.5,
                    minHeight: 56,
                    ...(shouldHighlight && {
                      '&:hover': {
                        bgcolor: 'primary.100',
                      },
                    }),
                  }}
                >
                  <ScheduleIcon color={shouldHighlight ? 'primary' : 'action'} sx={{ mr: 2 }} />
                  <ListItemText
                    primary={`${generatePaperId(queueGame.paper_id)}: ${queueGame.team1_name} vs ${queueGame.team2_name}`}
                    secondary={`n. klo ${formatTime(queueGame.schedule_start)}, kenttä ${queueGame.court_id}`}
                    primaryTypographyProps={shouldHighlight ? { fontWeight: 600 } : undefined}
                  />
                  {shouldHighlight && (
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        ml: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      SEURAAVA
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ei tulevia pelejä
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Manual Game Start - text button like FAQ */}
        <Button
          variant="text"
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          component={RouterLink}
          to={games.length > 0 ? `/judge/game/${games[0].id}` : "/judge/new"}
          disabled={games.length > 0}
          sx={{ color: 'text.secondary', mb: 1 }}
        >
          Peli ei listalla? Syötä käsin
        </Button>

        <Button
          variant="text"
          fullWidth
          size="small"
          startIcon={<HelpIcon />}
          component={RouterLink}
          to="/judge/faq"
          sx={{ color: 'text.secondary' }}
        >
          Kinkkiset tilanteet (FAQ)
        </Button>
      </Paper>

      {/* Messages */}
      <MessageList messages={messages} />

      {/* Finished Games */}
      {userFinishedGames.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Valmiit pelit
          </Typography>
          <List disablePadding>
            {userFinishedGames.slice(0, 5).map((game) => {
              const result = getGameResult(game);
              const isDraw = result.winner === 'draw';

              return (
                <ListItem
                  key={game.id}
                  sx={{
                    py: 1.5,
                    borderLeft: 4,
                    borderColor: game.cancelled
                      ? 'error.main'
                      : isDraw
                        ? 'warning.main'
                        : 'success.main',
                    pl: 2,
                    mb: 1,
                    bgcolor: game.cancelled
                      ? 'error.50'
                      : isDraw
                        ? 'warning.50'
                        : 'success.50',
                    borderRadius: 1,
                  }}
                  secondaryAction={
                    !game.cancelled && games.length === 0 && (
                      <IconButton
                        edge="end"
                        aria-label="muokkaa"
                        onClick={() => handleOpenReopenDialog(game)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <EditIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 4 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {game.id}
                        </Typography>
                        {!game.cancelled && result.winner && (
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                              color: isDraw ? 'warning.dark' : 'success.dark',
                            }}
                          >
                            {result.team1Total} - {result.team2Total}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      game.cancelled ? (
                        <Typography variant="body2" color="error.main">
                          Peruttu
                        </Typography>
                      ) : (
                        <Box>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontWeight: result.winner === 'team1' ? 700 : 400,
                              color: result.winner === 'team1' ? 'success.dark' : 'text.secondary',
                            }}
                          >
                            {game.team1.name}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' vs '}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontWeight: result.winner === 'team2' ? 700 : 400,
                              color: result.winner === 'team2' ? 'success.dark' : 'text.secondary',
                            }}
                          >
                            {game.team2.name}
                          </Typography>
                        </Box>
                      )
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          {/* Show remaining games in accordion if more than 5 */}
          {userFinishedGames.length > 5 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                bgcolor: 'transparent',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 0,
                  minHeight: 40,
                  '& .MuiAccordionSummary-content': { my: 0 },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Näytä kaikki pelit ({userFinishedGames.length - 5} lisää)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                <List disablePadding>
                  {userFinishedGames.slice(5).map((game) => {
                    const result = getGameResult(game);
                    const isDraw = result.winner === 'draw';

                    return (
                      <ListItem
                        key={game.id}
                        sx={{
                          py: 1.5,
                          borderLeft: 4,
                          borderColor: game.cancelled
                            ? 'error.main'
                            : isDraw
                              ? 'warning.main'
                              : 'success.main',
                          pl: 2,
                          mb: 1,
                          bgcolor: game.cancelled
                            ? 'error.50'
                            : isDraw
                              ? 'warning.50'
                              : 'success.50',
                          borderRadius: 1,
                        }}
                        secondaryAction={
                          !game.cancelled && games.length === 0 && (
                            <IconButton
                              edge="end"
                              aria-label="muokkaa"
                              onClick={() => handleOpenReopenDialog(game)}
                              sx={{ color: 'text.secondary' }}
                            >
                              <EditIcon />
                            </IconButton>
                          )
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 4 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {game.id}
                              </Typography>
                              {!game.cancelled && result.winner && (
                                <Typography
                                  variant="h6"
                                  fontWeight={700}
                                  sx={{
                                    color: isDraw ? 'warning.dark' : 'success.dark',
                                  }}
                                >
                                  {result.team1Total} - {result.team2Total}
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            game.cancelled ? (
                              <Typography variant="body2" color="error.main">
                                Peruttu
                              </Typography>
                            ) : (
                              <Box>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    fontWeight: result.winner === 'team1' ? 700 : 400,
                                    color: result.winner === 'team1' ? 'success.dark' : 'text.secondary',
                                  }}
                                >
                                  {game.team1.name}
                                </Typography>
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {' vs '}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    fontWeight: result.winner === 'team2' ? 700 : 400,
                                    color: result.winner === 'team2' ? 'success.dark' : 'text.secondary',
                                  }}
                                >
                                  {game.team2.name}
                                </Typography>
                              </Box>
                            )
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Paper>
      )}

      {/* Reopen Confirmation Dialog */}
      <Dialog
        open={reopenDialogOpen}
        onClose={handleCloseReopenDialog}
        aria-labelledby="reopen-dialog-title"
        aria-describedby="reopen-dialog-description"
      >
        <DialogTitle id="reopen-dialog-title">
          Avaa peli uudelleen?
        </DialogTitle>
        <DialogContent>
          {reopenError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reopenError}
            </Alert>
          )}
          <DialogContentText id="reopen-dialog-description">
            Haluatko avata pelin <strong>{gameToReopen?.id}</strong> uudelleen tuloksen muokkaamiseksi?
            <br /><br />
            <Alert severity="warning" sx={{ mt: 1 }}>
              Huomio: Tämä voi vaikuttaa lohkon tuloksiin ja jatkopeleihin.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseReopenDialog}
            color="inherit"
            disabled={submitReopenMutation.isPending}
          >
            Peruuta
          </Button>
          <Button
            onClick={handleConfirmReopen}
            color="warning"
            variant="contained"
            disabled={submitReopenMutation.isPending}
          >
            {submitReopenMutation.isPending ? 'Avataan...' : 'Avaa uudelleen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


